// v2.14.4 — Sticky CTA "Devenir Pro" affiché en bas de /mon-compte.
// - Si tier ∈ {premium, partner} : affiche badge "💎 Membre Premium" (no upsell)
// - Sinon : CTA "Boost ta visibilité — Devenir Pro 🚀"
// - Dismissible via croix, persisté 7 jours dans localStorage.

import { useState, useEffect } from "react";
import ProPricingModal from "@/components/ProPricingModal";

const TIER_COLORS = {
  partner: "#A855F7",
  premium: "#F59E0B",
  basic: "#3B82F6",
  free: "#64748B",
};

const DISMISS_KEY = "civ_ai_pro_cta_dismissed_until";
const TIER_KEY = "civ_ai_user_tier";
const SEVEN_DAYS_MS = 7 * 24 * 3600 * 1000;

function readTier() {
  try {
    const v = localStorage.getItem(TIER_KEY);
    if (v && ["free", "basic", "premium", "partner"].includes(v)) return v;
  } catch (_) {}
  return "free";
}
function readDismissedUntil() {
  try {
    const v = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  } catch (_) { return 0; }
}

export default function ProStickyCTA({ userId }) {
  const [tier, setTier] = useState(() => readTier());
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState(() => readDismissedUntil());

  // Recheck localStorage on mount in case another tab updated it.
  useEffect(() => {
    const sync = () => { setTier(readTier()); setDismissedUntil(readDismissedUntil()); };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const isPaid = tier === "premium" || tier === "partner";
  const isDismissed = Date.now() < dismissedUntil;

  if (isDismissed && !isPaid) return null;

  const dismiss = () => {
    const until = Date.now() + SEVEN_DAYS_MS;
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch (_) {}
    setDismissedUntil(until);
  };

  const onTierUpdated = (newTier) => {
    setTier(newTier);
    // Auto-dismiss disable: si l'user vient de souscrire, on garde affiché le badge "Membre".
    try { localStorage.removeItem(DISMISS_KEY); } catch (_) {}
    setDismissedUntil(0);
  };

  const tierColor = TIER_COLORS[tier];

  if (isPaid) {
    return (
      <div
        data-testid="pro-membership-badge"
        data-tier={tier}
        style={{
          position: "fixed", bottom: 16, right: 16, zIndex: 1500,
          background: `linear-gradient(135deg, ${tierColor}, #0f172a)`,
          color: "#fff", padding: "10px 14px", borderRadius: 999,
          border: `1.5px solid ${tierColor}`,
          boxShadow: `0 6px 20px ${tierColor}40`,
          fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 8,
          cursor: "pointer", maxWidth: 220,
        }}
        onClick={() => setModalOpen(true)}
        title="Gérer mon abonnement"
      >
        💎 Membre <span style={{ textTransform: "capitalize" }}>{tier}</span>
        <ProPricingModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          currentTier={tier}
          onTierUpdated={onTierUpdated}
          userId={userId}
        />
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="pro-sticky-cta"
        data-tier={tier}
        style={{
          position: "fixed", bottom: 16, right: 16, zIndex: 1500,
          background: "linear-gradient(135deg, #F59E0B, #EA580C)",
          color: "#fff", padding: "12px 16px 12px 14px", borderRadius: 14,
          boxShadow: "0 8px 24px rgba(234,88,12,0.45)",
          border: "1px solid rgba(255,255,255,0.15)",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 320,
          fontSize: 13, fontWeight: 600,
          animation: "civaiSlideUp 320ms ease-out",
        }}
      >
        <style>{`@keyframes civaiSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>
        <span style={{ fontSize: 22 }}>💎</span>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 2 }}>
          <strong style={{ fontSize: 13 }}>Boost ta visibilité</strong>
          <small style={{ fontSize: 11, opacity: 0.95, fontWeight: 400 }}>
            Apparais en top des recherches du chat
          </small>
        </div>
        <button
          data-testid="pro-sticky-cta-open"
          onClick={() => setModalOpen(true)}
          style={{
            background: "#fff", color: "#EA580C", border: "none",
            padding: "7px 12px", borderRadius: 8, cursor: "pointer",
            fontWeight: 700, fontSize: 12, whiteSpace: "nowrap",
          }}
        >Devenir Pro 🚀</button>
        <button
          data-testid="pro-sticky-cta-dismiss"
          onClick={dismiss}
          title="Fermer pour 7 jours"
          style={{
            background: "transparent", color: "#fff", border: "none",
            cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1,
            opacity: 0.7,
          }}
        >×</button>
      </div>
      <ProPricingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        currentTier={tier}
        onTierUpdated={onTierUpdated}
        userId={userId}
      />
    </>
  );
}
